import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDateTime } from "../utils/dateFormat";
import { naturalCompare } from "../utils/sort";
import { InputField, ReadOnlyField, SelectField } from "../components/preliminary-notification/primitives";

const FIELD_LABELS = {
  parentJurisdictionCode: "PARENT_JURISDICTION_CODE",
  agreementRequired: "AGREEMENT_REQ",
  agreementType: "AGREEMENT_TYPE",
  authName: "AUTH_NAME",
  authTitle: "AUTH_TITLE",
  authPhone: "AUTH_PHONE",
  authEmail: "AUTH_EMAIL",
  agreementJurisdictionName: "AGREEMENT_JURISDICTION_NAME",
  groupAgreementRequired: "GROUP_AGREEMENT_REQUIRED",
  groupAgreementType: "GROUP_AGREEMENT_TYPE",
  groupName: "GROUP_NAME",
  groupAddress: "GROUP_ADDRESS",
  parentAgencyCodes: "PARENT_AGENCY_CODES",
  groupAuthorizedSigner: "GROUP_AUTHORIZED_SIGNER",
  groupAuthorizedSignerTitle: "GROUP_AUTH_SIGN_TITLE",
  groupAuthSignEmail: "GROUP_AUTH_SIGN_EMAIL",
  groupAuthSignPhone: "GROUP_AUTH_SIGN_PHONE"
};

const emptySigner = {
  parentJurisdictionCode: "",
  authName: "",
  authTitle: "",
  authPhone: "",
  authEmail: "",
  agreementType: "",
  agreementReq: "",
  agreementJurisdictionName: "",
  groupId: ""
};

const emptyGroup = {
  groupName: "",
  groupAddress: "",
  groupAuthorizedSigner: "",
  groupTitle: "",
  groupEmail: "",
  groupPhone: "",
  groupAgreementType: "",
  groupAgreementRequired: ""
};

const SIGNER_CONDITIONAL_KEYS = ["authName", "authTitle", "agreementType", "agreementJurisdictionName"];
// These fields get cleared when AGREEMENT_REQ is set to "N" and restored when set back to "Y".
const GROUP_CONDITIONAL_KEYS = ["groupAuthorizedSigner", "groupTitle", "groupAgreementType"];

// Stash is dialog-scoped: saving as "N" discards the prior values permanently.
function makeAgreementToggle(setEditing, triggerKey, conditionalKeys) {
  return (next) =>
    setEditing((e) => {
      if (!e) return e;
      const previous = e.form[triggerKey];
      if (previous === "Y" && next !== "Y") {
        const stash = {};
        const blanks = {};
        for (const k of conditionalKeys) {
          stash[k] = e.form[k];
          blanks[k] = "";
        }
        return { ...e, stash, form: { ...e.form, ...blanks, [triggerKey]: next } };
      }
      if (previous !== "Y" && next === "Y" && e.stash) {
        const restored = {};
        for (const k of conditionalKeys) {
          if (e.stash[k] !== undefined) restored[k] = e.stash[k];
        }
        return { ...e, stash: undefined, form: { ...e.form, ...restored, [triggerKey]: next } };
      }
      return { ...e, form: { ...e.form, [triggerKey]: next } };
    });
}

// Distinct parent jurisdiction codes across the signer rows, sorted naturally.
// The signers endpoint carries no parent *name*, so the bare code is all the
// Agency filter can offer — see CLAUDE.md.
function parentCodeOptions(signers, include) {
  const seen = new Set();
  for (const s of signers || []) {
    if (!s.parentJurisdictionCode) continue;
    if (include && !include(s)) continue;
    seen.add(s.parentJurisdictionCode);
  }
  return Array.from(seen).sort(naturalCompare);
}

export default function AdminAuthorizedSigner() {
  const apiFetch = useApiFetch();
  const [tab, setTab] = useState(0);

  const [signers, setSigners] = useState(null);
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      const [sRes, gRes] = await Promise.all([
        apiFetch("/admin/authorized-signer/signers"),
        apiFetch("/admin/authorized-signer/groups")
      ]);
      if (!sRes.ok) throw new Error(`Signers HTTP ${sRes.status}`);
      if (!gRes.ok) throw new Error(`Groups HTTP ${gRes.status}`);
      setSigners(await sRes.json());
      setGroups(await gRes.json());
    } catch (err) {
      setError(err.message || "Failed to load.");
    }
  }, [apiFetch]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Authorized Signers & Groups
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Changes to the Authorized Signer or Group fields will only reflect in the Submission page and Reports
        if the agency submits the Online Preliminary Notification of Direct Charges after your changes are
        made.
      </Alert>

      {error && (
        <Box
          sx={{ p: 2, mb: 2, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
        >
          {error}
        </Box>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Authorized Signers" />
        <Tab label="Parent Jurisdiction Groups" />
      </Tabs>

      {tab === 0 && <SignersTab apiFetch={apiFetch} signers={signers} groups={groups} onChanged={loadAll} />}
      {tab === 1 && <GroupsTab apiFetch={apiFetch} groups={groups} signers={signers} onChanged={loadAll} />}
    </Box>
  );
}

function SignersTab({ apiFetch, signers, groups, onChanged }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editing, setEditing] = useState(null); // null | { mode, id?, form }
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  // Per-row change-log viewer. null = closed; otherwise { entityType, entityId, label }
  const [historyTarget, setHistoryTarget] = useState(null);
  // [] = no Agency filter; otherwise an array of parent jurisdiction codes.
  const [agencyFilters, setAgencyFilters] = useState([]);

  const groupOptions = useMemo(() => groups || [], [groups]);
  const groupNameById = useMemo(
    () => new Map((groups || []).map((g) => [g.groupId, g.groupName])),
    [groups]
  );

  const agencyOptions = useMemo(() => parentCodeOptions(signers), [signers]);
  const filteredSigners = useMemo(() => {
    if (!signers) return [];
    if (!agencyFilters.length) return signers;
    const codeSet = new Set(agencyFilters);
    return signers.filter((s) => codeSet.has(s.parentJurisdictionCode));
  }, [signers, agencyFilters]);

  const clearFilter = () => {
    setAgencyFilters([]);
    setPage(0);
  };

  const openCreate = () => {
    setFormError(null);
    setEditing({ mode: "create", form: { ...emptySigner } });
  };
  const openEdit = (row) => {
    setFormError(null);
    setEditing({
      mode: "edit",
      id: row.id,
      groupLinked: row.groupId != null,
      form: {
        parentJurisdictionCode: row.parentJurisdictionCode || "",
        authName: row.authName || "",
        authTitle: row.authTitle || "",
        authPhone: row.authPhone || "",
        authEmail: row.authEmail || "",
        agreementType: row.agreementType || "",
        agreementReq: row.agreementReq || "",
        agreementJurisdictionName: row.agreementJurisdictionName || "",
        groupId: row.groupId == null ? "" : String(row.groupId)
      }
    });
  };
  const closeDialog = () => {
    if (saving) return;
    setEditing(null);
  };
  const setField = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const setAgreementReq = makeAgreementToggle(setEditing, "agreementReq", SIGNER_CONDITIONAL_KEYS);

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const isEdit = editing.mode === "edit";
      const url = isEdit
        ? `/admin/authorized-signer/signers/${editing.id}`
        : "/admin/authorized-signer/signers";
      const body = {
        ...editing.form,
        groupId: editing.form.groupId === "" ? null : Number(editing.form.groupId)
      };
      const res = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      setEditing(null);
      await onChanged();
    } catch (err) {
      setFormError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (signers === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pagedRows = filteredSigners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Warn (don't block) when creating a signer for a parent code that already
  // has at least one signer row — multi-signer is supported, but the admin
  // should know they're adding a duplicate.
  const typedCode = editing?.form?.parentJurisdictionCode?.trim().toLowerCase() || "";
  const duplicateExisting =
    editing?.mode === "create" && typedCode
      ? signers.find((s) => (s.parentJurisdictionCode || "").trim().toLowerCase() === typedCode)
      : null;

  // Only PARENT_JURISDICTION_CODE and AGREEMENT_REQ are always required.
  // Every other signer field is only required when AGREEMENT_REQ === "Y".
  // Group-linked signers are controlled by the group.
  const signerAgreementRequired = editing?.form.agreementReq === "Y";
  const signerMissingRequired =
    editing && !editing.groupLinked
      ? !(
          editing.form.parentJurisdictionCode?.trim() &&
          editing.form.agreementReq &&
          (!signerAgreementRequired ||
            (editing.form.authName?.trim() &&
              editing.form.authTitle?.trim() &&
              editing.form.agreementType &&
              editing.form.agreementJurisdictionName?.trim()))
        )
      : false;

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={1}
          options={agencyOptions}
          value={agencyFilters}
          onChange={(_, v) => {
            setAgencyFilters(v);
            setPage(0);
          }}
          sx={{ minWidth: 300 }}
          renderInput={(params) => <TextField {...params} label="Agency" size="small" />}
        />
        {agencyFilters.length > 0 && <Button onClick={clearFilter}>Clear filter</Button>}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Authorized Signer
        </Button>
      </Stack>

      {agencyFilters.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Showing {filteredSigners.length} of {signers.length} signer
          {signers.length === 1 ? "" : "s"}.
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead
            sx={{
              "& .MuiTableCell-head": { bgcolor: colors.darkGray, color: "white", fontWeight: 700 }
            }}
          >
            <TableRow>
              <TableCell>{FIELD_LABELS.parentJurisdictionCode}</TableCell>
              <TableCell>{FIELD_LABELS.agreementType}</TableCell>
              <TableCell>{FIELD_LABELS.agreementRequired}</TableCell>
              <TableCell>{FIELD_LABELS.authName}</TableCell>
              <TableCell>{FIELD_LABELS.authTitle}</TableCell>
              <TableCell>{FIELD_LABELS.authPhone}</TableCell>
              <TableCell>{FIELD_LABELS.authEmail}</TableCell>
              <TableCell>{FIELD_LABELS.agreementJurisdictionName}</TableCell>
              <TableCell>{FIELD_LABELS.groupName}</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.parentJurisdictionCode || "—"}</TableCell>
                <TableCell>{r.agreementType || "—"}</TableCell>
                <TableCell>{r.agreementReq || "—"}</TableCell>
                <TableCell>{r.authName || "—"}</TableCell>
                <TableCell>{r.authTitle || "—"}</TableCell>
                <TableCell>{r.authPhone || "—"}</TableCell>
                <TableCell>{r.authEmail || "—"}</TableCell>
                <TableCell>{r.agreementJurisdictionName || "—"}</TableCell>
                <TableCell>{r.groupId != null ? groupNameById.get(r.groupId) || r.groupId : "—"}</TableCell>
                <TableCell>
                  {r.updatedAt ? (
                    <Tooltip
                      arrow
                      title={
                        r.createdAt
                          ? `Created by ${r.createdByName || r.createdBy || "—"} on ${fmtDateTime(r.createdAt)}`
                          : "No creation record"
                      }
                    >
                      <span>
                        {fmtDateTime(r.updatedAt)}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {r.updatedByName || r.updatedBy || "—"}
                        </Typography>
                      </span>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    <Tooltip title="Edit" arrow>
                      <IconButton size="small" onClick={() => openEdit(r)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View change history" arrow>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setHistoryTarget({
                            entityType: "AuthorizedSigner",
                            entityId: String(r.id),
                            label: r.authName || r.parentJurisdictionCode || `Signer ${r.id}`,
                            groupLinked: r.groupId != null
                          })
                        }
                        aria-label="History"
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {pagedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                  {agencyFilters.length > 0
                    ? "No signers match the current filter."
                    : "No authorized signers yet."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ChangeLogDialog apiFetch={apiFetch} target={historyTarget} onClose={() => setHistoryTarget(null)} />

      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 25, 50, 100]}
        count={filteredSigners.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <Dialog open={editing !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing?.mode === "edit" ? "Edit Authorized Signer" : "New Authorized Signer"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editing?.groupLinked && (
              <Alert severity="info">
                This signer belongs to a parent jurisdiction group. Name, title, and agreement fields are
                controlled by the group and can only be edited from the Parent Jurisdiction Groups tab. Only
                the group assignment can be changed here.
              </Alert>
            )}
            {!editing?.groupLinked && (
              <>
                {editing?.mode === "edit" ? (
                  <ReadOnlyField
                    label={FIELD_LABELS.parentJurisdictionCode}
                    required
                    value={editing?.form.parentJurisdictionCode || ""}
                  />
                ) : (
                  <InputField
                    label={FIELD_LABELS.parentJurisdictionCode}
                    required
                    placeholder={FIELD_LABELS.parentJurisdictionCode}
                    value={editing?.form.parentJurisdictionCode || ""}
                    onChange={(e) => setField("parentJurisdictionCode", e.target.value)}
                  />
                )}
                {editing?.mode === "create" && duplicateExisting && (
                  <Alert severity="warning">
                    A signer already exists for <strong>{duplicateExisting.parentJurisdictionCode}</strong>
                    {duplicateExisting.authName ? ` (${duplicateExisting.authName})` : ""}. You can still save
                    — this will add a second signer row for the same parent.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectField
                      label={FIELD_LABELS.agreementRequired}
                      required
                      value={editing?.form.agreementReq || ""}
                      onChange={(e) => setAgreementReq(e.target.value)}
                      options={[
                        { value: "Y", label: "Y" },
                        { value: "N", label: "N" }
                      ]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectField
                      label={FIELD_LABELS.agreementType}
                      required={signerAgreementRequired}
                      disabled={!signerAgreementRequired}
                      value={editing?.form.agreementType || ""}
                      onChange={(e) => setField("agreementType", e.target.value)}
                      options={[
                        { value: "City", label: "City" },
                        { value: "District", label: "District" }
                      ]}
                    />
                  </Grid>
                </Grid>
                <InputField
                  label={FIELD_LABELS.authName}
                  required={signerAgreementRequired}
                  disabled={!signerAgreementRequired}
                  placeholder={FIELD_LABELS.authName}
                  value={editing?.form.authName || ""}
                  onChange={(e) => setField("authName", e.target.value)}
                />
                <InputField
                  label={FIELD_LABELS.authTitle}
                  required={signerAgreementRequired}
                  disabled={!signerAgreementRequired}
                  placeholder={FIELD_LABELS.authTitle}
                  value={editing?.form.authTitle || ""}
                  onChange={(e) => setField("authTitle", e.target.value)}
                />
                <InputField
                  label={FIELD_LABELS.agreementJurisdictionName}
                  required={signerAgreementRequired}
                  disabled={!signerAgreementRequired}
                  placeholder={FIELD_LABELS.agreementJurisdictionName}
                  value={editing?.form.agreementJurisdictionName || ""}
                  onChange={(e) => setField("agreementJurisdictionName", e.target.value)}
                />
              </>
            )}
            <SelectField
              label="Group"
              value={editing?.form.groupId ?? ""}
              onChange={(e) => setField("groupId", e.target.value)}
              options={[
                { value: "", label: "— None —" },
                ...groupOptions.map((g) => ({
                  value: String(g.groupId),
                  label: `${g.groupName || `Group ${g.groupId}`} (${g.groupId})`
                }))
              ]}
            />
            {formError && (
              <Box
                sx={{ p: 1.5, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
              >
                {formError}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || signerMissingRequired}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function GroupsTab({ apiFetch, groups, signers, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  // Per-row change-log viewer. null = closed; otherwise { entityType, entityId, label }
  const [historyTarget, setHistoryTarget] = useState(null);
  // [] = no Agency filter; otherwise an array of parent jurisdiction codes.
  // Matches groups that have any of these agencies as a member.
  const [agencyFilters, setAgencyFilters] = useState([]);

  // Roll up distinct ParentJurisdictionCode per GroupId from the signers list
  // (already loaded by the parent). One pass, sorted for stable display.
  const parentCodesByGroupId = useMemo(() => {
    const map = new Map();
    for (const s of signers || []) {
      if (s.groupId == null || !s.parentJurisdictionCode) continue;
      const arr = map.get(s.groupId) || [];
      if (!arr.includes(s.parentJurisdictionCode)) arr.push(s.parentJurisdictionCode);
      map.set(s.groupId, arr);
    }
    for (const arr of map.values()) arr.sort();
    return map;
  }, [signers]);

  // Only codes that actually belong to a group — offering ungrouped codes here
  // would give options that can never match a row.
  const agencyOptions = useMemo(
    () => parentCodeOptions(signers, (s) => s.groupId != null),
    [signers]
  );
  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    if (!agencyFilters.length) return groups;
    const codeSet = new Set(agencyFilters);
    return groups.filter((g) => (parentCodesByGroupId.get(g.groupId) || []).some((c) => codeSet.has(c)));
  }, [groups, agencyFilters, parentCodesByGroupId]);

  const openCreate = () => {
    setFormError(null);
    setEditing({ mode: "create", form: { ...emptyGroup } });
  };
  const openEdit = (row) => {
    setFormError(null);
    setEditing({
      mode: "edit",
      groupId: row.groupId,
      form: {
        groupName: row.groupName || "",
        groupAddress: row.groupAddress || "",
        groupAuthorizedSigner: row.groupAuthorizedSigner || "",
        groupTitle: row.groupTitle || "",
        groupEmail: row.groupEmail || "",
        groupPhone: row.groupPhone || "",
        groupAgreementType: row.groupAgreementType || "",
        groupAgreementRequired: row.groupAgreementRequired || ""
      }
    });
  };
  const closeDialog = () => {
    if (saving) return;
    setEditing(null);
  };
  const setField = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));

  const setGroupAgreementRequired = makeAgreementToggle(
    setEditing,
    "groupAgreementRequired",
    GROUP_CONDITIONAL_KEYS
  );

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const isEdit = editing.mode === "edit";
      const url = isEdit
        ? `/admin/authorized-signer/groups/${editing.groupId}`
        : "/admin/authorized-signer/groups";
      const res = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editing.form)
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      setEditing(null);
      await onChanged();
    } catch (err) {
      setFormError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (groups === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // GROUP_AGREEMENT_REQUIRED, GROUP_NAME, and GROUP_ADDRESS are always required.
  // The signer/title/agreement-type fields are only required when GROUP_AGREEMENT_REQUIRED === "Y".
  const groupAgreementRequired = editing?.form.groupAgreementRequired === "Y";
  const groupMissingRequired = editing
    ? !(
        editing.form.groupAgreementRequired &&
        editing.form.groupName?.trim() &&
        editing.form.groupAddress?.trim() &&
        (!groupAgreementRequired ||
          (editing.form.groupAuthorizedSigner?.trim() &&
            editing.form.groupTitle?.trim() &&
            editing.form.groupAgreementType))
      )
    : false;

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={1}
          options={agencyOptions}
          value={agencyFilters}
          onChange={(_, v) => setAgencyFilters(v)}
          sx={{ minWidth: 300 }}
          renderInput={(params) => <TextField {...params} label="Agency" size="small" />}
        />
        {agencyFilters.length > 0 && <Button onClick={() => setAgencyFilters([])}>Clear filter</Button>}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Group
        </Button>
      </Stack>

      {agencyFilters.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Showing {filteredGroups.length} of {groups.length} group
          {groups.length === 1 ? "" : "s"}.
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead
            sx={{ "& .MuiTableCell-head": { bgcolor: colors.darkGray, color: "white", fontWeight: 700 } }}
          >
            <TableRow>
              <TableCell>{FIELD_LABELS.groupName}</TableCell>
              <TableCell sx={{ width: 140 }}>{FIELD_LABELS.parentAgencyCodes}</TableCell>
              <TableCell>{FIELD_LABELS.groupAddress}</TableCell>
              <TableCell>{FIELD_LABELS.groupAuthorizedSigner}</TableCell>
              <TableCell>{FIELD_LABELS.groupAuthorizedSignerTitle}</TableCell>
              <TableCell>{FIELD_LABELS.groupAuthSignEmail}</TableCell>
              <TableCell>{FIELD_LABELS.groupAuthSignPhone}</TableCell>
              <TableCell>{FIELD_LABELS.groupAgreementType}</TableCell>
              <TableCell>{FIELD_LABELS.groupAgreementRequired}</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGroups.map((r) => (
              <TableRow key={r.groupId} hover>
                <TableCell>{r.groupName || "—"}</TableCell>
                <TableCell>
                  <ParentCodesCell codes={parentCodesByGroupId.get(r.groupId) || []} />
                </TableCell>
                <TableCell sx={{ maxWidth: 280, whiteSpace: "normal" }}>{r.groupAddress || "—"}</TableCell>
                <TableCell>{r.groupAuthorizedSigner || "—"}</TableCell>
                <TableCell>{r.groupTitle || "—"}</TableCell>
                <TableCell>{r.groupEmail || "—"}</TableCell>
                <TableCell>{r.groupPhone || "—"}</TableCell>
                <TableCell>{r.groupAgreementType || "—"}</TableCell>
                <TableCell>{r.groupAgreementRequired || "—"}</TableCell>
                <TableCell>
                  {r.updatedAt ? (
                    <Tooltip
                      arrow
                      title={
                        r.createdAt
                          ? `Created by ${r.createdByName || r.createdBy || "—"} on ${fmtDateTime(r.createdAt)}`
                          : "No creation record"
                      }
                    >
                      <span>
                        {fmtDateTime(r.updatedAt)}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {r.updatedByName || r.updatedBy || "—"}
                        </Typography>
                      </span>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    <Tooltip title="Edit" arrow>
                      <IconButton size="small" onClick={() => openEdit(r)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View change history" arrow>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setHistoryTarget({
                            entityType: "ParentJurisdictionGroup",
                            entityId: String(r.groupId),
                            label: r.groupName || `Group ${r.groupId}`
                          })
                        }
                        aria-label="History"
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {filteredGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                  {agencyFilters.length > 0
                    ? "No groups match the current filter."
                    : "No parent jurisdiction groups yet."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ChangeLogDialog apiFetch={apiFetch} target={historyTarget} onClose={() => setHistoryTarget(null)} />

      <Dialog open={editing !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.mode === "edit" ? "Edit Group" : "New Group"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label={FIELD_LABELS.groupAgreementRequired}
                  required
                  value={editing?.form.groupAgreementRequired || ""}
                  onChange={(e) => setGroupAgreementRequired(e.target.value)}
                  options={[
                    { value: "Y", label: "Y" },
                    { value: "N", label: "N" }
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectField
                  label={FIELD_LABELS.groupAgreementType}
                  required={groupAgreementRequired}
                  disabled={!groupAgreementRequired}
                  value={editing?.form.groupAgreementType || ""}
                  onChange={(e) => setField("groupAgreementType", e.target.value)}
                  options={[
                    { value: "City", label: "City" },
                    { value: "District", label: "District" }
                  ]}
                />
              </Grid>
            </Grid>
            <InputField
              label={FIELD_LABELS.groupName}
              required
              placeholder={FIELD_LABELS.groupName}
              value={editing?.form.groupName || ""}
              onChange={(e) => setField("groupName", e.target.value)}
            />
            <InputField
              label={FIELD_LABELS.groupAddress}
              required
              placeholder={FIELD_LABELS.groupAddress}
              value={editing?.form.groupAddress || ""}
              onChange={(e) => setField("groupAddress", e.target.value)}
              multiline
              minRows={2}
            />
            <InputField
              label={FIELD_LABELS.groupAuthorizedSigner}
              required={groupAgreementRequired}
              disabled={!groupAgreementRequired}
              placeholder={FIELD_LABELS.groupAuthorizedSigner}
              value={editing?.form.groupAuthorizedSigner || ""}
              onChange={(e) => setField("groupAuthorizedSigner", e.target.value)}
            />
            <InputField
              label={FIELD_LABELS.groupAuthorizedSignerTitle}
              required={groupAgreementRequired}
              disabled={!groupAgreementRequired}
              placeholder={FIELD_LABELS.groupAuthorizedSignerTitle}
              value={editing?.form.groupTitle || ""}
              onChange={(e) => setField("groupTitle", e.target.value)}
            />
            {formError && (
              <Box
                sx={{ p: 1.5, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
              >
                {formError}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || groupMissingRequired}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Cell for the "Parent Codes" column on the Groups tab. Shows the count as a
// small button; clicking opens a popover listing each code as a Chip.
function ParentCodesCell({ codes }) {
  const [anchor, setAnchor] = useState(null);
  if (!codes || codes.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <>
      <Tooltip title="Click to view parent codes" arrow>
        <Avatar
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: 28,
            height: 28,
            fontSize: 13,
            fontWeight: 600,
            bgcolor: "primary.main",
            cursor: "pointer",
            "&:hover": { bgcolor: "primary.dark" }
          }}
        >
          {codes.length}
        </Avatar>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 360 }}>
          {codes.map((c) => (
            <Chip key={c} label={c} size="small" sx={{ fontFamily: "monospace" }} />
          ))}
        </Box>
      </Popover>
    </>
  );
}

// Generic per-entity change-log viewer. Calls the same backend endpoint
// (GET /admin/changelog/{entityType}/{entityId}) regardless of which admin
// table the row belongs to — drop-in for any future History icon.
//
// target: null = closed; { entityType, entityId, label } = open + fetch.
function ChangeLogDialog({ apiFetch, target, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!target) {
      setRows(null);
      setError(null);
      return;
    }
    let cancelled = false;
    apiFetch(
      `/admin/changelog/${encodeURIComponent(target.entityType)}/${encodeURIComponent(target.entityId)}`
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load change history.");
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch, target]);

  return (
    <Dialog open={target !== null} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Change history{target?.label ? ` — ${target.label}` : ""}</DialogTitle>
      <DialogContent>
        {target?.groupLinked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This signer belongs to a parent jurisdiction group. Edits are made in the Parent Jurisdiction
            Groups tab and reflected here for information only.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!error && rows === null && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {rows && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No changes recorded yet.
          </Typography>
        )}
        {rows && rows.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead
                sx={{ "& .MuiTableCell-head": { bgcolor: colors.darkGray, color: "white", fontWeight: 700 } }}
              >
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Old</TableCell>
                  <TableCell>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(r.changedAt)}</TableCell>
                    <TableCell>
                      <Tooltip title={r.changedBy} arrow>
                        <span>{r.changedByName || r.changedBy}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{r.action}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{r.fieldName || "—"}</TableCell>
                    <TableCell sx={{ color: "text.secondary", maxWidth: 220, whiteSpace: "normal" }}>
                      {r.oldValue ?? "—"}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, whiteSpace: "normal" }}>{r.newValue ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
